import { useContext } from 'react';

import { classNames } from '../../utils/style';
import {
  textBodyLarge,
  textCaption,
  textTitle3,
} from '../../styles/typography';

import CardContentContext from './CardContentContext';

export enum CardType {
  BlogCard,
  InspirationalCard,
  WithoutBgCard,
  MenuCard,
  ResourceCard,
}

export const useApplyCapsTextStyle = () => {
  const context = useContext(CardContentContext);

  switch (context?.cardType) {
    case CardType.BlogCard:
      return 'mb-12';
    case CardType.InspirationalCard:
      return 'mb-16';
    case CardType.WithoutBgCard:
      return '';
    default:
      return 'mt-24';
  }
};

export const useApplyTagsStyle = () => {
  const context = useContext(CardContentContext);

  switch (context?.cardType) {
    case CardType.InspirationalCard:
      return 'mb-16';
    case CardType.MenuCard:
      return 'mb-8';
    default:
      return 'mb-24';
  }
};

export const useApplyLogoStyle = () => {
  const context = useContext(CardContentContext);

  if (context?.cardType === CardType.WithoutBgCard) return 'mb-24';

  return 'mb-16';
};

export const useApplyTitleStyle = () => {
  const context = useContext(CardContentContext);

  switch (context?.cardType) {
    case CardType.BlogCard: {
      return classNames(
        'line-clamp-3 sm:line-clamp-2 md:line-clamp-3 h-auto',
        textBodyLarge,
        context?.hasSubtitleHover && !context?.isHovered
          ? 'sm:group-hover:h-48 md:group-hover:h-88'
          : '',
      );
    }
    case CardType.InspirationalCard:
      return classNames(
        'line-clamp-3 h-88 sm:h-64 md:line-clamp-4 md:h-136 md:-mb-12',
        textTitle3,
      );
    case CardType.MenuCard:
      return 'text-1';
    default:
      return classNames(
        'line-clamp-3 sm:line-clamp-2 sm:-mb-4 md:line-clamp-3 lg:line-clamp-4 md:mb-8',
        textTitle3,
      );
  }
};

export const useApplySubtitleStyle = () => {
  const context = useContext(CardContentContext);

  switch (context?.cardType) {
    case CardType.BlogCard:
      return 'md:visible -mb-4 sm:mb-0 lg:line-clamp-4 text-ellipsis';
    case CardType.InspirationalCard:
      return 'md:visible md:line-clamp-3 h-64';
    case CardType.MenuCard:
      return classNames(
        'sm:visible sm:block text-neutral-101',
        textCaption,
      );
    default:
      return 'md:visible md:line-clamp-3 lg:line-clamp-4 text-ellipsis';
  }
};

export const useApplyDisplaySubtitleOnSmallDeviceStyle = () => {
  const context = useContext(CardContentContext);

  switch (context?.cardType) {
    case CardType.BlogCard:
      return 'line-clamp-6 sm:line-clamp-3';
    case CardType.WithoutBgCard:
      return 'line-clamp-6 sm:line-clamp-3';
    default:
      return '';
  }
};

export const useApplyHasSubtitleHoverStyle = () => {
  const context = useContext(CardContentContext);

  if (context?.cardType === CardType.BlogCard)
    return context?.hasSubtitleHover
      ? `md:block h-0 overflow-hidden md:group-hover:h-88 transition-all duration-500 ease-in-out`
      : 'md:line-clamp-3';

  return '';
};

export const useApplyCtaButtonStyle = () => {
  return 'mt-24';
};

export const useApplyCardContentWithoutTagStyle = (): string => {
  const context = useContext(CardContentContext);

  return !context?.hasTags &&
    context?.cardType !== CardType.WithoutBgCard &&
    context?.hasLogo
    ? 'mt-64'
    : '';
};
